import React, { useMemo } from 'react'
import styled from 'styled-components'
import { skip, take, sortBy } from 'lib/array/arrayUtils'
import useToggle from 'hooks/useToggle'
import { pluralizeToString } from 'lib/string/pluralize'
import SmallToggleButton from 'LuxKit/Button/SmallToggleButton'
import Expander from 'components/utils/Expander'
import ExperienceTicketItem from './ExperienceTicketItem'
import Heading from 'LuxKit/Typography/Heading'

const TicketOptions = styled.div`
  > *:not(:last-child) {
    border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};
  }
`

const AdditionalTicketOptions = styled(TicketOptions)`
  border-top: 1px solid ${props => props.theme.palette.neutral.default.six};
`

const CenteredToggle = styled(SmallToggleButton)`
  display: flex;
  margin: 0 auto;
`

interface Props {
  onTicketCountChange?: (ticket: App.ExperienceBookingTicketView, nextCount: number) => void;
  ticketCounts: any;
  ticketOptions: Array<App.ExperienceBookingTicketView>;
  purchaseLimitRemaining?: number;
  purchaseLimitReason?: App.ExperiencePurchaseLimit['maxReason'];
  orderMode?: App.ExperienceOrderMode;
  className?: string;
}

const initialMaxVisibleOptions = 4

function ExperienceTicketSelection(props: Props) {
  const {
    ticketOptions,
    ticketCounts,
    onTicketCountChange,
    className,
    purchaseLimitRemaining,
    purchaseLimitReason,
    orderMode = 'booking',
  } = props
  const [showAll, toggleShowAll] = useToggle()

  const sortedTickets = useMemo(() => sortBy(ticketOptions, ticket => !!ticket.isExtra, 'asc'), [ticketOptions])

  return <div className={className}>
    {sortedTickets.length > 0 && <>
      <Heading variant="heading6">Select an option</Heading>
      <TicketOptions>
        {take(sortedTickets, initialMaxVisibleOptions).map(ticket =>
          <ExperienceTicketItem
            key={ticket.id}
            ticket={ticket}
            count={ticketCounts[ticket.id]}
            onTicketCountChange={onTicketCountChange}
            purchaseLimitRemaining={purchaseLimitRemaining}
            purchaseLimitReason={purchaseLimitReason}
            orderMode={orderMode}
          />)}
      </TicketOptions>
      {sortedTickets.length > initialMaxVisibleOptions && <>
        <Expander expanded={showAll}>
          <AdditionalTicketOptions>
            {skip(sortedTickets, initialMaxVisibleOptions).map(ticket =>
              <ExperienceTicketItem
                key={ticket.id}
                ticket={ticket}
                count={ticketCounts[ticket.id]}
                onTicketCountChange={onTicketCountChange}
                purchaseLimitRemaining={purchaseLimitRemaining}
                purchaseLimitReason={purchaseLimitReason}
                orderMode={orderMode}
              />)}
          </AdditionalTicketOptions>
        </Expander>
        <CenteredToggle
          onClick={toggleShowAll}
          size="medium"
          on={showAll}
        >
          {!showAll && <>View {pluralizeToString('more option', sortedTickets.length - initialMaxVisibleOptions)}</>}
          {showAll && <>View less options</>}
        </CenteredToggle>
      </>}
    </>}
  </div>
}

export default ExperienceTicketSelection
